import { useEffect, useRef } from 'react';
import { useLoaderData } from 'react-router-dom';

import { TOP_NAVBAR_HEIGHT } from 'src/pages/LessonPage/children/Sidebar';
import { sendTrackEvent } from 'src/utils/analytics';
import { isStandalone } from 'src/pages/LessonPage/utils/isStandalone';
import { usePreviewMode } from 'src/hooks/usePreviewMode';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { useProgressState } from 'src/store/progress/progressStore';
import { getSlug } from 'src/utils/slug';
import { LessonLoaderProps } from 'src/pages/LessonPage/loader';

export const cleanupObserver = (sections: { id: string }[], seenSectionObserver: IntersectionObserver): void => {
  for (const section of [...sections, { id: 'quiz' }]) {
    const element = document.getElementById(`${section.id}`);

    if (element) {
      seenSectionObserver.unobserve(element);
    }
  }
};

export const useIntersectionObserverForProgressTracking = (): void => {
  const preview = usePreviewMode();
  const { user } = useAtlassianUser();

  const { course, lesson } = useLoaderData() as LessonLoaderProps;

  const [, { completeSection }] = useProgressState();

  const seenSections = useRef<string[]>([]);

  useEffect(() => {
    if (!lesson) {
      return;
    }

    const seenSectionObserver: IntersectionObserver = new IntersectionObserver(
      (entries) => {
        const sectionName = entries[0].target.id;
        const sectionId = lesson.sectionListCollection.items.find((s) => s.id === sectionName)?.sys.id;

        if (
          entries.length === 1 &&
          sectionId &&
          !seenSections.current.includes(sectionId) &&
          sectionId &&
          sectionName !== 'quiz'
        ) {
          seenSections.current.push(sectionId);

          completeSection(sectionId, lesson, preview, user);

          sendTrackEvent(
            'lessonScreen',
            'lessonSection',
            'completed',
            {
              courseSlug: getSlug(course),
              lessonSlug: getSlug(lesson),
              sectionId,
              isStandalone: isStandalone(),
            },
            user?.account_id,
          );
        }
      },
      { threshold: 1, rootMargin: `${TOP_NAVBAR_HEIGHT}px 0px -75% 0px` },
    );

    const sections = lesson.sectionListCollection.items ?? [];

    for (const section of [...sections, { id: 'quiz' }]) {
      const element = document.getElementById(`${section.id}`);

      if (element) {
        seenSectionObserver.observe(element);
      }
    }

    return () => cleanupObserver(sections, seenSectionObserver);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson]);

  useEffect(() => {
    seenSections.current = [];
  }, [lesson]);
};
